'use client'

import { useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { useForm } from 'react-hook-form'
import { SquareChevronRight } from 'lucide-react'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/ui/form'
import { useApplySystemSettings, useUnsavedChangesGuard } from '@/hooks'
import { useMetaStore } from '@/store'
import { SystemSettingsParams } from '@/types/setting'
import { UnsavedChangesGuardDialog } from '@/components/partials'

const SystemSettingsForm = () => {
  const t = useTranslations()
  const { metaState } = useMetaStore()
  const { mutate: applyMutation, isPending } = useApplySystemSettings()

  const formSchema = z.object({
    maintenanceMode: z.boolean().default(false).optional(),
    clearCache: z.boolean().default(false).optional(),
    clearLogs: z.boolean().default(false).optional()
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      maintenanceMode: false,
      clearCache: false,
      clearLogs: false
    }
  })

  const { isNavigationBlocked, confirmNavigation, cancelNavigation } = useUnsavedChangesGuard(form.formState.isDirty)

  useEffect(() => {
    if (metaState.response?.generalSettings) {
      form.setValue('maintenanceMode', metaState.response.generalSettings.maintenanceMode ?? false)
    }
  }, [metaState.response, form])

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    applyMutation(data as SystemSettingsParams, {
      onSuccess: async () => {
        const { getMeta } = useMetaStore.getState()
        await getMeta()
        form.setValue('clearCache', false)
        form.setValue('clearLogs', false)
        toast.success(t('settings_applied_successfully'))
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || t('something_went_wrong'))
      }
    })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-6">
        <div>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="maintenanceMode"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                  <div>
                    <FormLabel>{t('maintenance_mode')}</FormLabel>
                    <FormDescription>{t('maintenance_mode_description')}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} disabled={isPending} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="clearCache"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                  <div>
                    <FormLabel>{t('clear_cache')}</FormLabel>
                    <FormDescription>{t('clear_cache_description')}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} disabled={isPending} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="clearLogs"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                  <div>
                    <FormLabel>{t('clear_logs')}</FormLabel>
                    <FormDescription>{t('clear_logs_description')}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} disabled={isPending} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
        <Button type="submit" loading={isPending} disabled={isPending}>
          <SquareChevronRight />
          {t('apply')}
        </Button>
        <UnsavedChangesGuardDialog
          open={isNavigationBlocked}
          onConfirm={confirmNavigation}
          onCancel={cancelNavigation}
        />
      </form>
    </Form>
  )
}

export default SystemSettingsForm
